import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSave,
  faHouse,
  faArrowLeft,
  faArrowRight,
  faXmark,
  faPlus,
  faEye,
  faEyeSlash,
} from "@fortawesome/free-solid-svg-icons";
import { requestGetClasses,
  requestAddPayment,
  requestGetPayment,
  requestGetPaymentValues,
  requestSavePaymentValues,
  requestGetAllPayments,
  auth } from "../utils/resource";
import { useNavigate } from "react-router-dom";
import "../styles/PaymentControl.css";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  BarController,
  Title as ChartTitle,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { Line } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  BarController,
  ChartTitle,
  Tooltip,
  Legend,
  Filler
);

ChartJS.defaults.font.family = '"Space Grotesk", sans-serif';

const PaymentControl = () => {
  const navigate = useNavigate();
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedMonthPayment, setSelectedMonthPayment] = useState(
    new Date().toISOString().slice(0, 7)
  );
  const [selectedMonthUnpayment, setSelectedMonthUnpayment] = useState(
    new Date().toISOString().slice(0, 7)
  );
  const [currentPagePayment, setCurrentPagePayment] = useState(1);
  const [currentPageUnpayment, setCurrentPageUnpayment] = useState(1);
  const [isAdding, setIsAdding] = useState(false);
  const [myClasses, setMyClasses] = useState([]);
  const [studentClassesCount, setStudentClassesCount] = useState({});
  const [originalStudents, setOriginalStudents] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState({});
  const [paymentData, setPaymentData] = useState([]);
  const [studentsWithPendingPayments, setStudentsWithPendingPayments] =
    useState([]);
  const [currentDay] = useState(new Date().getDate());

  const [defaultValues, setDefaultValues] = useState([140, 210, 300]);
  const [showRevenue, setShowRevenue] = useState(false);
  const [allPayments, setAllPayments] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 600);
  const [chartPageIndex, setChartPageIndex] = useState(0);

  useEffect(() => {
    requestGetAllPayments(setAllPayments);
  }, [isAdding]);

  useEffect(() => {
    setChartPageIndex(0);
  }, [isAdding, selectedMonthPayment, selectedMonthUnpayment]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 600);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);


  const historicalStats = React.useMemo(() => {
    const currentMonthStr = new Date().toISOString().slice(0, 7);
    
    const pastMonthDocs = allPayments.filter((doc) => {
      if (!doc.month || doc.month >= currentMonthStr) {
        return false;
      }
      const data = doc.paymentData || [];
      const totalStudents = data.length;
      if (totalStudents === 0) {
        return false;
      }
      const paidStudentsCount = data.filter(
        (student) => student.paid && student.paymentDay
      ).length;
      return paidStudentsCount >= totalStudents / 2;
    });
    
    const validPastMonthsCount = pastMonthDocs.length;
    
    let totalPastDay10 = 0;
    let totalPastDay20 = 0;
    
    pastMonthDocs.forEach((doc) => {
      const data = doc.paymentData || [];
      data.forEach((student) => {
        if (student.paid) {
          const day = parseInt(student.paymentDay, 10);
          if (!isNaN(day)) {
            const value = defaultValues[student.classes - 1] || 0;
            if (day <= 10) {
              totalPastDay10 += value;
            }
            if (day <= 20) {
              totalPastDay20 += value;
            }
          }
        }
      });
    });
    
    const averageDay10 = validPastMonthsCount > 0 ? totalPastDay10 / validPastMonthsCount : 0;
    const averageDay20 = validPastMonthsCount > 0 ? totalPastDay20 / validPastMonthsCount : 0;
    
    const activeMonth = isAdding ? selectedMonthPayment : selectedMonthUnpayment;
    const isViewingCurrentMonth = activeMonth === currentMonthStr;

    let currentDay10 = 0;
    let currentDay20 = 0;

    if (isViewingCurrentMonth) {
      originalStudents.forEach((student) => {
        const paid = selectedStudents[student.id] || false;
        if (paid) {
          const studentDaySelect = filteredStudents.find((s) => s.id === student.id)?.paymentDay;
          const day = parseInt(studentDaySelect || student.paymentDay, 10);
          if (!isNaN(day)) {
            const value = defaultValues[student.classes - 1] || 0;
            if (day <= 10) {
              currentDay10 += value;
            }
            if (day <= 20) {
              currentDay20 += value;
            }
          }
        }
      });
    } else {
      const currentMonthDoc = allPayments.find(
        (doc) => doc.month === activeMonth
      );
      if (currentMonthDoc) {
        const data = currentMonthDoc.paymentData || [];
        data.forEach((student) => {
          if (student.paid) {
            const day = parseInt(student.paymentDay, 10);
            if (!isNaN(day)) {
              const value = defaultValues[student.classes - 1] || 0;
              if (day <= 10) {
                currentDay10 += value;
              }
              if (day <= 20) {
                currentDay20 += value;
              }
            }
          }
        });
      }
    }

    return {
      averageDay10,
      averageDay20,
      currentDay10,
      currentDay20,
    };
  }, [allPayments, defaultValues, isAdding, selectedMonthPayment, selectedMonthUnpayment, originalStudents, selectedStudents, filteredStudents]);

  const advancedStats = React.useMemo(() => {
    const currentMonthStr = new Date().toISOString().slice(0, 7);
    
    const activeMonth = isAdding ? selectedMonthPayment : selectedMonthUnpayment;
    const isViewingCurrentMonth = activeMonth === currentMonthStr;

    let totalExpectedCurrent = 0;
    let totalPaidCurrent = 0;
    let paidStudentsCountCurrent = 0;
    
    let count1Class = 0;
    let count2Classes = 0;
    let count3Classes = 0;

    if (isViewingCurrentMonth) {
      originalStudents.forEach((student) => {
        const value = defaultValues[student.classes - 1] || 0;
        totalExpectedCurrent += value;
        
        if (student.classes === 1) count1Class++;
        else if (student.classes === 2) count2Classes++;
        else if (student.classes === 3) count3Classes++;

        const paid = selectedStudents[student.id] || false;
        if (paid) {
          totalPaidCurrent += value;
          paidStudentsCountCurrent++;
        }
      });
    } else {
      const currentMonthDoc = allPayments.find((doc) => doc.month === activeMonth);
      if (currentMonthDoc) {
        const data = currentMonthDoc.paymentData || [];
        data.forEach((student) => {
          const value = defaultValues[student.classes - 1] || 0;
          totalExpectedCurrent += value;

          if (student.classes === 1) count1Class++;
          else if (student.classes === 2) count2Classes++;
          else if (student.classes === 3) count3Classes++;

          if (student.paid) {
            totalPaidCurrent += value;
            paidStudentsCountCurrent++;
          }
        });
      }
    }

    const pendingCurrent = totalExpectedCurrent - totalPaidCurrent;
    const pendingRate = totalExpectedCurrent > 0 ? (pendingCurrent / totalExpectedCurrent) * 100 : 0;
    const paidRate = totalExpectedCurrent > 0 ? (totalPaidCurrent / totalExpectedCurrent) * 100 : 0;
    const ticketAverage = paidStudentsCountCurrent > 0 ? totalPaidCurrent / paidStudentsCountCurrent : 0;

    const pastMonthDocs = allPayments.filter((doc) => {
      if (!doc.month || doc.month >= currentMonthStr) {
        return false;
      }
      const data = doc.paymentData || [];
      const totalStudents = data.length;
      if (totalStudents === 0) return false;
      const paidStudentsCount = data.filter((s) => s.paid && s.paymentDay).length;
      return paidStudentsCount >= totalStudents / 2;
    });

    const dayRevenues = {};
    pastMonthDocs.forEach((doc) => {
      const data = doc.paymentData || [];
      data.forEach((student) => {
        if (student.paid && student.paymentDay) {
          const day = parseInt(student.paymentDay, 10);
          if (!isNaN(day)) {
            const value = defaultValues[student.classes - 1] || 0;
            dayRevenues[day] = (dayRevenues[day] || 0) + value;
          }
        }
      });
    });

    let peakDay = null;
    let maxDayRevenue = 0;
    Object.keys(dayRevenues).forEach((dayStr) => {
      const day = parseInt(dayStr, 10);
      const rev = dayRevenues[day];
      if (rev > maxDayRevenue) {
        maxDayRevenue = rev;
        peakDay = day;
      }
    });

    const averagePeakDayRevenue = pastMonthDocs.length > 0 && peakDay ? dayRevenues[peakDay] / pastMonthDocs.length : 0;
    const totalStudentsCount = count1Class + count2Classes + count3Classes;
    const pendingStudentsCountCurrent = totalStudentsCount - paidStudentsCountCurrent;

    return {
      totalExpectedCurrent,
      totalPaidCurrent,
      pendingCurrent,
      pendingRate,
      paidRate,
      ticketAverage,
      count1Class,
      count2Classes,
      count3Classes,
      totalStudentsCount,
      paidStudentsCountCurrent,
      pendingStudentsCountCurrent,
      peakDay,
      averagePeakDayRevenue,
    };
  }, [allPayments, defaultValues, isAdding, selectedMonthPayment, selectedMonthUnpayment, originalStudents, selectedStudents]);

  const chartData = React.useMemo(() => {
    const currentMonthStr = new Date().toISOString().slice(0, 7);
    
    const monthMap = {};
    const paidStudentsMap = {};
    allPayments.forEach((doc) => {
      if (doc.month) {
        if (doc.month < currentMonthStr) {
          const data = doc.paymentData || [];
          const totalStudents = data.length;
          if (totalStudents === 0) return;
          const paidStudentsCount = data.filter(
            (student) => student.paid
          ).length;
          if (paidStudentsCount < totalStudents / 2) return;
        }

        monthMap[doc.month] = (doc.paymentData || []).reduce((total, student) => {
          if (student.paid) {
            return total + (defaultValues[student.classes - 1] || 0);
          }
          return total;
        }, 0);

        paidStudentsMap[doc.month] = (doc.paymentData || []).filter(
          (student) => student.paid
        ).length;
      }
    });
    
    const activeMonth = isAdding ? selectedMonthPayment : selectedMonthUnpayment;
    const isViewingCurrentMonth = activeMonth === currentMonthStr;
    
    if (isViewingCurrentMonth) {
      let currentMonthRevenue = 0;
      let currentMonthPaidStudents = 0;
      originalStudents.forEach((student) => {
        const paid = selectedStudents[student.id] || false;
        if (paid) {
          currentMonthRevenue += defaultValues[student.classes - 1] || 0;
          currentMonthPaidStudents += 1;
        }
      });
      monthMap[currentMonthStr] = currentMonthRevenue;
      paidStudentsMap[currentMonthStr] = currentMonthPaidStudents;
    } else {
      if (monthMap[currentMonthStr] === undefined) {
        monthMap[currentMonthStr] = 0;
        paidStudentsMap[currentMonthStr] = 0;
      }
    }
    
    const sortedMonths = Object.keys(monthMap).sort();
    let visibleMonths = sortedMonths;
    if (isMobile) {
      const monthsPerPage = 8;
      const totalMonths = sortedMonths.length;
      const endIdx = totalMonths - chartPageIndex * monthsPerPage;
      const startIdx = Math.max(0, endIdx - monthsPerPage);
      if (endIdx > 0) {
        visibleMonths = sortedMonths.slice(startIdx, endIdx);
      } else {
        visibleMonths = [];
      }
    }
    
    const labels = visibleMonths.map((m) => {
      const [year, month] = m.split("-");
      return `${month}/${year}`;
    });
    
    const data = visibleMonths.map((m) => monthMap[m]);
    const paidStudents = visibleMonths.map((m) => paidStudentsMap[m] || 0);
    
    return {
      labels,
      data,
      paidStudents,
      hasPrev: isMobile && (sortedMonths.length - (chartPageIndex + 1) * 8 > 0),
      hasNext: isMobile && (chartPageIndex > 0),
      totalMonths: sortedMonths.length
    };
  }, [allPayments, defaultValues, isAdding, selectedMonthPayment, selectedMonthUnpayment, originalStudents, selectedStudents, isMobile, chartPageIndex]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: "top",
        labels: {
          color: "#e2e8f0",
          font: {
            family: '"Space Grotesk", sans-serif',
            size: isMobile ? 9 : 11,
          },
          boxWidth: isMobile ? 8 : 12,
          boxHeight: isMobile ? 8 : 12,
          usePointStyle: true,
        },
      },
      tooltip: {
        backgroundColor: "#151f28",
        titleColor: "#fff",
        bodyColor: "#fff",
        borderColor: "rgba(255, 255, 255, 0.1)",
        borderWidth: 1,
        callbacks: {
          label: function (context) {
            let label = context.dataset.label || "";
            if (label) {
              label += ": ";
            }
            if (context.datasetIndex === 0) {
              if (context.parsed.y !== null) {
                label += new Intl.NumberFormat("pt-BR", {
                  style: "currency",
                  currency: "BRL",
                }).format(context.parsed.y);
              }
              const index = context.dataIndex;
              if (chartData.paidStudents && chartData.paidStudents[index] !== undefined) {
                const paidCount = chartData.paidStudents[index];
                label += ` (${paidCount} ${paidCount === 1 ? "aluno pago" : "alunos pagos"})`;
              }
            } else {
              if (context.parsed.y !== null) {
                label += context.parsed.y + (context.parsed.y === 1 ? " aluno pago" : " alunos pagos");
              }
              const index = context.dataIndex;
              if (chartData.data && chartData.data[index] !== undefined) {
                const revenue = chartData.data[index];
                label += ` (${new Intl.NumberFormat("pt-BR", {
                  style: "currency",
                  currency: "BRL",
                }).format(revenue)})`;
              }
            }
            return label;
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          color: "rgba(255, 255, 255, 0.05)",
        },
        ticks: {
          color: "#a0aec0",
          maxRotation: 0,
          autoSkip: true,
          font: {
            family: '"Space Grotesk", sans-serif',
            size: isMobile ? 8 : 11,
          },
        },
      },
      y: {
        type: "linear",
        display: true,
        position: "left",
        grid: {
          color: "rgba(255, 255, 255, 0.05)",
        },
        ticks: {
          color: "#a0aec0",
          font: {
            family: '"Space Grotesk", sans-serif',
            size: isMobile ? 8 : 11,
          },
          callback: function (value) {
            if (isMobile) {
              if (value >= 1000) {
                const formatted = value / 1000;
                return "R$ " + (Number.isInteger(formatted) ? formatted : formatted.toFixed(1)) + "k";
              }
              return "R$ " + value;
            }
            return "R$ " + value;
          },
        },
      },
      y1: {
        type: "linear",
        display: true,
        position: "right",
        grid: {
          drawOnChartArea: false,
        },
        ticks: {
          color: "#a0aec0",
          font: {
            family: '"Space Grotesk", sans-serif',
            size: isMobile ? 8 : 11,
          },
          callback: function (value) {
            return value;
          },
        },
        min: 0,
      },
    },
  };

  const chartDataConfig = {
    labels: chartData.labels,
    datasets: [
      {
        label: "Ganhos",
        data: chartData.data,
        yAxisID: "y",
        type: "line",
        borderColor: "#4ade80",
        backgroundColor: (context) => {
          const chart = context.chart;
          const { ctx, chartArea } = chart;
          if (!chartArea) {
            return "rgba(74, 222, 128, 0.1)";
          }
          const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
          gradient.addColorStop(0, "rgba(74, 222, 128, 0.3)");
          gradient.addColorStop(1, "rgba(74, 222, 128, 0.0)");
          return gradient;
        },
        fill: true,
        tension: 0.4,
        pointBackgroundColor: "#4ade80",
        pointBorderColor: "#fff",
        pointHoverBackgroundColor: "#fff",
        pointHoverBorderColor: "#4ade80",
        pointRadius: isMobile ? 3 : 4,
        pointHoverRadius: isMobile ? 5 : 6,
        order: 1,
      },
      {
        label: "Alunos Pagos",
        data: chartData.paidStudents,
        yAxisID: "y1",
        type: "bar",
        borderColor: "#38bdf8",
        backgroundColor: "rgba(56, 189, 248, 0.2)",
        hoverBackgroundColor: "rgba(56, 189, 248, 0.4)",
        borderRadius: isMobile ? 3 : 4,
        borderWidth: 1,
        barThickness: isMobile ? 10 : 16,
        order: 2,
      },
    ],
  };

  const countClasses = () => {
    return myClasses.reduce((count, dayEntry) => {
      Object.values(dayEntry.time).forEach((students) => {
        students.forEach((student) => {
          count[student.name] = (count[student.name] || 0) + 1;
        });
      });
      return count;
    }, {});
  };

  useEffect(() => {
    requestGetPaymentValues(setDefaultValues);
  }, []);

  

  useEffect(() => {
    if (isAdding) {
      requestGetPayment(selectedMonthPayment, setPaymentData);
      setSearchTerm("");
      setSelectedMonthPayment(new Date().toISOString().slice(0, 7));
      setCurrentPagePayment(1);
    } else {
      requestGetPayment(selectedMonthUnpayment, setPaymentData);
    }
  }, [isAdding]);

  useEffect(() => {
    if (myClasses.length > 0) {
      setStudentClassesCount(countClasses());
    }
  }, [myClasses]);

  useEffect(() => {
    requestGetPayment(selectedMonthPayment, setPaymentData);
  }, [selectedMonthPayment]);

  useEffect(() => {
    requestGetPayment(selectedMonthUnpayment, setPaymentData);
  }, [selectedMonthUnpayment]);

  useEffect(() => {
    if (Object.keys(paymentData).length > 0) {
      const studentsArray = paymentData.paymentData
        .filter((student) => studentClassesCount[student.name])
        .map((student) => {
          const classesFromStudentCount =
            studentClassesCount[student.name] || student.classes;
          return {
            name: student.name,
            classes: Math.max(student.classes, classesFromStudentCount),
            paid: student.paid,
            paymentDay: student.paymentDay,
          };
        });

      const newStudents = Object.keys(studentClassesCount)
        .filter(
          (name) =>
            !paymentData.paymentData.some((student) => student.name === name)
        )
        .map((name) => ({
          name,
          classes: studentClassesCount[name],
          paid: false,
          paymentDay: "",
        }));

      const allStudents = [...studentsArray, ...newStudents]
        .sort((a, b) => a.name.localeCompare(b.name, "pt", { sensitivity: "base" }))
        .map((student, index) => ({
          ...student,
          id: index,
        }));

      const initialSelectedStudents = {};
      allStudents.forEach((student) => {
        initialSelectedStudents[student.id] = student.paid;
      });

      setFilteredStudents(allStudents);
      setOriginalStudents(allStudents);
      setSelectedStudents(initialSelectedStudents);

      const unpaidStudents = allStudents.filter((student) => !student.paid);
      setStudentsWithPendingPayments(unpaidStudents);
    } else {
      if (Object.keys(studentClassesCount).length > 0) {
        const studentsArray = Object.keys(studentClassesCount)
          .map((name) => ({
            name,
            classes: studentClassesCount[name],
            paid: false,
            paymentDay: "",
          }))
          .sort((a, b) => a.name.localeCompare(b.name, "pt", { sensitivity: "base" }))
          .map((student, index) => ({
            ...student,
            id: index,
          }));

        const initialSelectedStudents = {};
        studentsArray.forEach((student) => {
          initialSelectedStudents[student.id] = student.paid;
        });

        setFilteredStudents(studentsArray);
        setOriginalStudents(studentsArray);
        setStudentsWithPendingPayments(studentsArray);
        setSelectedStudents(initialSelectedStudents);
      }
    }
  }, [paymentData, studentClassesCount]);

  useEffect(() => {
    requestGetClasses(auth?.currentUser?.email, setMyClasses);
  }, []);

  useEffect(() => {
    const results = searchTerm
      ? originalStudents.filter((student) =>
          student.name.toLowerCase().includes(searchTerm.toLowerCase())
        )
      : originalStudents;

    setFilteredStudents(results);
  }, [searchTerm, originalStudents]);

  useEffect(() => {
    setCurrentPagePayment(1);
  }, [searchTerm, selectedMonthPayment]);

  const handlePaymentCheck = (id) => {
    setSelectedStudents((prev) => {
      const isPaid = !prev[id];
      setOriginalStudents((prevStudents) =>
        prevStudents.map((student) => {
          if (student.id === id) {
            return {
              ...student,
              paymentDay: isPaid ? (student.paymentDay || String(currentDay)) : "",
            };
          }
          return student;
        })
      );
      return {
        ...prev,
        [id]: isPaid,
      };
    });
  };

  const handleDayChange = (id, day) => {
    setOriginalStudents((prev) =>
      prev.map((student) =>
        student.id === id ? { ...student, paymentDay: day } : student
      )
    );
  };

  const generateDays = () => Array.from({ length: 31 }, (_, i) => i + 1);

  const handleCancel = () => navigate("/dashboard");

  const handlePageChangePayment = (pageNumber) => {
    setCurrentPagePayment(pageNumber);
  };

  const handlePageChangeUnpayment = (pageNumber) => {
    setCurrentPageUnpayment(pageNumber);
  };

  const handlePrevChartPage = () => {
    setChartPageIndex((prev) => prev + 1);
  };

  const handleNextChartPage = () => {
    setChartPageIndex((prev) => Math.max(0, prev - 1));
  };

  const handleSaveDefaultValues = () => {
    requestSavePaymentValues(defaultValues);
    alert("Valores padrão salvos com sucesso!");
  };

  const savePayment = () => {
    const paymentData = originalStudents.map((student) => ({
      name: student.name,
      classes: student.classes,
      paid: selectedStudents[student.id] || false,
      paymentDay: student.paymentDay || "",
    }));
    requestAddPayment(
      selectedMonthPayment,
      paymentData,
      auth?.currentUser?.email
    );
    setIsAdding(false);
  };

  const itemsPerPage = 5;
  const currentStudents = filteredStudents.slice(
    (currentPagePayment - 1) * itemsPerPage,
    currentPagePayment * itemsPerPage
  );
  const totalPages = Math.ceil(filteredStudents.length / itemsPerPage);

  const unpaidItemsPerPage = 5;
  const currentUnpaidStudents = studentsWithPendingPayments.slice(
    (currentPageUnpayment - 1) * unpaidItemsPerPage,
    currentPageUnpayment * unpaidItemsPerPage
  );
  const totalUnpaidPages = Math.ceil(
    studentsWithPendingPayments.length / unpaidItemsPerPage
  );



  const renderDynamicPageNumbers = (
    currentPage,
    totalPages,
    handlePageChange
  ) => {
    const pages = [];

    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(
          <button
            key={i}
            onClick={() => handlePageChange(i)}
            className={i === currentPage ? "active" : ""}
          >
            {i}
          </button>
        );
      }
      return pages;
    }

    pages.push(
      <button
        key={1}
        onClick={() => handlePageChange(1)}
        className={1 === currentPage ? "active" : ""}
      >
        1
      </button>
    );

    if (currentPage > 4) {
      pages.push(
        <span key="ellipsis-start" className="pagination-ellipsis">
          ...
        </span>
      );
    }

    let start = Math.max(2, currentPage - 1);
    let end = Math.min(totalPages - 1, currentPage + 1);

    if (currentPage <= 3) {
      end = 4;
    } else if (currentPage >= totalPages - 2) {
      start = totalPages - 3;
    }

    for (let i = start; i <= end; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={i === currentPage ? "active" : ""}
        >
          {i}
        </button>
      );
    }

    if (currentPage < totalPages - 3) {
      pages.push(
        <span key="ellipsis-end" className="pagination-ellipsis">
          ...
        </span>
      );
    }

    pages.push(
      <button
        key={totalPages}
        onClick={() => handlePageChange(totalPages)}
        className={totalPages === currentPage ? "active" : ""}
      >
        {totalPages}
      </button>
    );

    return pages;
  };

  return (
    <div className="payment-container">
      <main className="payment-main">
        <div className="button-bar">
          {isAdding ? (
            <>
              <FontAwesomeIcon
                icon={faSave}
                className="icon save-icon"
                onClick={savePayment}
                title="Salvar"
              />
              <FontAwesomeIcon
                icon={faXmark}
                className="icon cancel-icon"
                onClick={() => setIsAdding(false)}
                title="Cancelar"
              />
            </>
          ) : (
            <>
              <FontAwesomeIcon
                icon={faPlus}
                className="icon add-icon"
                onClick={() => setIsAdding(true)}
                title="Adicionar Estudante"
              />
              <FontAwesomeIcon
                icon={faHouse}
                className="icon cancel-icon"
                onClick={handleCancel}
                title="Cancelar"
              />
            </>
          )}
        </div>

        {!isAdding && (
          <div>
            <div className="faturamento-container">
              <div className="faturamento" style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "20px", flexWrap: "wrap" }}>
                <input
                  type="month"
                  id="month-select-unpayment-bar"
                  value={selectedMonthUnpayment}
                  onChange={(e) => setSelectedMonthUnpayment(e.target.value)}
                  style={{
                    padding: "4px 8px",
                    borderRadius: "4px",
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                    background: "#fff",
                    color: "#151f28",
                    fontSize: "1rem",
                    fontWeight: "bold",
                    fontFamily: "inherit",
                    cursor: "pointer"
                  }}
                />
                <span style={{ display: "flex", alignItems: "center" }}>
                  Faturamento do mês: R${" "}
                  {showRevenue ? advancedStats.totalPaidCurrent.toFixed(2) : "***"}
                  <FontAwesomeIcon
                    icon={showRevenue ? faEye : faEyeSlash}
                    onClick={() => setShowRevenue(!showRevenue)}
                    title={showRevenue ? "Ocultar faturamento" : "Mostrar faturamento"}
                    className="icon revenue-icon"
                    style={{ marginLeft: "10px", color: "#fff", cursor: "pointer", fontSize: "1.1rem" }}
                  />
                </span>
              </div>
            </div>
            {/* Seção de Estatísticas Avançadas */}
            <div className="historical-stats-container">
              {/* Card 1: Saúde de Cobrança (Pago vs Pendente vs Estimado) */}
              <div className="stat-card">
                <div className="stat-card-header">
                  <h3>Status de Cobrança</h3>
                  <span className="stat-subtitle">Recebido vs. Pendente vs. Estimado</span>
                </div>
                <div className="stat-values">
                  <div className="stat-value-item">
                    <span className="stat-label">Recebido</span>
                    <span className="stat-number highlight" style={{ color: "#4ade80" }}>
                      {showRevenue ? `R$ ${advancedStats.totalPaidCurrent.toFixed(2)}` : "R$ ***"}
                    </span>
                    <span className="stat-sub-number" style={{ color: "#a0aec0", fontSize: "0.78rem", marginTop: "4px" }}>
                      {advancedStats.paidStudentsCountCurrent} {advancedStats.paidStudentsCountCurrent === 1 ? 'aluno' : 'alunos'}
                    </span>
                  </div>
                  <div className="stat-value-item">
                    <span className="stat-label">Pendente</span>
                    <span className="stat-number" style={{ color: "#f87171" }}>
                      {showRevenue ? `R$ ${advancedStats.pendingCurrent.toFixed(2)}` : "R$ ***"}
                    </span>
                    <span className="stat-sub-number" style={{ color: "#a0aec0", fontSize: "0.78rem", marginTop: "4px" }}>
                      {advancedStats.pendingStudentsCountCurrent} {advancedStats.pendingStudentsCountCurrent === 1 ? 'aluno' : 'alunos'}
                    </span>
                  </div>
                  <div className="stat-value-item">
                    <span className="stat-label">Estimado</span>
                    <span className="stat-number" style={{ color: "#60a5fa" }}>
                      {showRevenue ? `R$ ${advancedStats.totalExpectedCurrent.toFixed(2)}` : "R$ ***"}
                    </span>
                    <span className="stat-sub-number" style={{ color: "#a0aec0", fontSize: "0.78rem", marginTop: "4px" }}>
                      {advancedStats.totalStudentsCount} {advancedStats.totalStudentsCount === 1 ? 'aluno' : 'alunos'}
                    </span>
                  </div>
                </div>
                {advancedStats.totalExpectedCurrent > 0 && (
                  <div className="stat-progress-section">
                    <div className="progress-bar-container" style={{ background: "rgba(248, 113, 113, 0.2)" }}>
                      <div
                        className="progress-bar-fill"
                        style={{
                          width: `${advancedStats.paidRate}%`,
                          background: "linear-gradient(95deg, #4ade80 0%, #22c55e 100%)",
                        }}
                      ></div>
                    </div>
                    <span className="progress-percentage">
                      <span>{advancedStats.paidRate.toFixed(0)}% Recebido</span>
                      <span style={{ color: "#f87171" }}>{advancedStats.pendingRate.toFixed(0)}% Pendente</span>
                    </span>
                  </div>
                )}
              </div>

              {/* Card 2: Eficiência & Fluxo de Caixa (Ticket Médio, Dia de Pico e Metas Dia 10/20) */}
              <div className="stat-card efficiency-card">
                <div className="stat-card-header">
                  <h3>Eficiência & Fluxo de Caixa</h3>
                  <span className="stat-subtitle">Histórico de arrecadação e metas do mês</span>
                </div>
                
                <div className="efficiency-metrics-row">
                  <div className="efficiency-metric-item">
                    <span className="stat-label">Ticket Médio</span>
                    <span className="stat-number highlight-blue">
                      {showRevenue ? `R$ ${advancedStats.ticketAverage.toFixed(2)}` : "R$ ***"}
                    </span>
                  </div>
                  <div className="efficiency-metric-item">
                    <span className="stat-label">Dia de Pico</span>
                    <span className="stat-number highlight-yellow">
                      {advancedStats.peakDay ? `Dia ${advancedStats.peakDay}` : "N/A"}
                    </span>
                  </div>
                  <div className="efficiency-metric-item">
                    <span className="stat-label">Pico Médio Histórico</span>
                    <span className="stat-number" style={{ color: "#e2e8f0" }}>
                      {showRevenue && advancedStats.peakDay ? `R$ ${advancedStats.averagePeakDayRevenue.toFixed(0)}` : "***"}
                    </span>
                  </div>
                </div>

                <hr className="stat-card-divider" />

                <div className="efficiency-goals-grid">
                  {/* Dia 10 */}
                  <div className="goal-section">
                    <div className="goal-header">
                      <h4>Até o Dia 10</h4>
                      <span className="goal-label-media">Média: {showRevenue ? `R$ ${historicalStats.averageDay10.toFixed(0)}` : "***"}</span>
                    </div>
                    <div className="goal-body">
                      <span className="goal-current">
                        Atual: <strong>{showRevenue ? `R$ ${historicalStats.currentDay10.toFixed(2)}` : "R$ ***"}</strong>
                      </span>
                      {historicalStats.averageDay10 > 0 && (
                        <div className="stat-progress-section">
                          <div className="progress-bar-container">
                            <div
                              className="progress-bar-fill"
                              style={{
                                width: `${Math.min(
                                  100,
                                  (historicalStats.currentDay10 / historicalStats.averageDay10) * 100
                                )}%`,
                              }}
                            ></div>
                          </div>
                          <span className="progress-percentage">
                            {((historicalStats.currentDay10 / historicalStats.averageDay10) * 100).toFixed(0)}% da média
                            {historicalStats.currentDay10 >= historicalStats.averageDay10 && (
                              <span className="badge-above-average">Meta batida!</span>
                            )}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Dia 20 */}
                  <div className="goal-section">
                    <div className="goal-header">
                      <h4>Até o Dia 20</h4>
                      <span className="goal-label-media">Média: {showRevenue ? `R$ ${historicalStats.averageDay20.toFixed(0)}` : "***"}</span>
                    </div>
                    <div className="goal-body">
                      <span className="goal-current">
                        Atual: <strong>{showRevenue ? `R$ ${historicalStats.currentDay20.toFixed(2)}` : "R$ ***"}</strong>
                      </span>
                      {historicalStats.averageDay20 > 0 && (
                        <div className="stat-progress-section">
                          <div className="progress-bar-container">
                            <div
                              className="progress-bar-fill"
                              style={{
                                width: `${Math.min(
                                  100,
                                  (historicalStats.currentDay20 / historicalStats.averageDay20) * 100
                                )}%`,
                              }}
                            ></div>
                          </div>
                          <span className="progress-percentage">
                            {((historicalStats.currentDay20 / historicalStats.averageDay20) * 100).toFixed(0)}% da média
                            {historicalStats.currentDay20 >= historicalStats.averageDay20 && (
                              <span className="badge-above-average">Meta batida!</span>
                            )}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Seção Horizontal de Distribuição de Planos */}
            {advancedStats.totalStudentsCount > 0 && (
              <div className="distribution-card">
                <div className="distribution-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                  <div>
                    <h3>Distribuição de Planos (Aulas Semanais)</h3>
                    <span className="stat-subtitle">Perfil de frequência dos alunos do mês</span>
                  </div>
                  <span className="dist-total-badge" style={{ background: "rgba(255, 255, 255, 0.08)", padding: "4px 10px", borderRadius: "12px", fontSize: "0.85rem", color: "#fff", fontWeight: "600" }}>
                    Total: {advancedStats.totalStudentsCount} {advancedStats.totalStudentsCount === 1 ? 'aluno' : 'alunos'}
                  </span>
                </div>
                <div className="dist-bar-container">
                  {advancedStats.count1Class > 0 && (
                    <div
                      className="dist-bar-fill class-1"
                      style={{
                        width: `${(advancedStats.count1Class / advancedStats.totalStudentsCount) * 100}%`,
                      }}
                      title={`1 Aula: ${advancedStats.count1Class} alunos`}
                    ></div>
                  )}
                  {advancedStats.count2Classes > 0 && (
                    <div
                      className="dist-bar-fill class-2"
                      style={{
                        width: `${(advancedStats.count2Classes / advancedStats.totalStudentsCount) * 100}%`,
                      }}
                      title={`2 Aulas: ${advancedStats.count2Classes} alunos`}
                    ></div>
                  )}
                  {advancedStats.count3Classes > 0 && (
                    <div
                      className="dist-bar-fill class-3"
                      style={{
                        width: `${(advancedStats.count3Classes / advancedStats.totalStudentsCount) * 100}%`,
                      }}
                      title={`3 Aulas: ${advancedStats.count3Classes} alunos`}
                    ></div>
                  )}
                </div>
                <div className="dist-legend">
                  <div className="legend-item">
                    <span className="legend-dot class-1"></span>
                    <span>
                      1 Aula: <strong>{advancedStats.count1Class}</strong> {advancedStats.count1Class === 1 ? 'aluno' : 'alunos'} ({((advancedStats.count1Class / advancedStats.totalStudentsCount) * 100).toFixed(0)}%)
                    </span>
                  </div>
                  <div className="legend-item">
                    <span className="legend-dot class-2"></span>
                    <span>
                      2 Aulas: <strong>{advancedStats.count2Classes}</strong> {advancedStats.count2Classes === 1 ? 'aluno' : 'alunos'} ({((advancedStats.count2Classes / advancedStats.totalStudentsCount) * 100).toFixed(0)}%)
                    </span>
                  </div>
                  <div className="legend-item">
                    <span className="legend-dot class-3"></span>
                    <span>
                      3 Aulas: <strong>{advancedStats.count3Classes}</strong> {advancedStats.count3Classes === 1 ? 'aluno' : 'alunos'} ({((advancedStats.count3Classes / advancedStats.totalStudentsCount) * 100).toFixed(0)}%)
                    </span>
                  </div>
                </div>
              </div>
            )}

            <div className="chart-card">
              <div className="chart-card-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                <div>
                  <h3>Evolução Mensal de Faturamento</h3>
                  <span className="chart-subtitle">Histórico de ganhos mensais acumulados</span>
                </div>
                {isMobile && (chartData.hasPrev || chartData.hasNext) && (
                  <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                    <button
                      onClick={handlePrevChartPage}
                      disabled={!chartData.hasPrev}
                      style={{
                        background: "rgba(255, 255, 255, 0.08)",
                        border: "1px solid rgba(255, 255, 255, 0.15)",
                        borderRadius: "6px",
                        color: chartData.hasPrev ? "#fff" : "rgba(255, 255, 255, 0.3)",
                        padding: "6px 10px",
                        cursor: chartData.hasPrev ? "pointer" : "default",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "0.85rem"
                      }}
                      title="Meses anteriores"
                    >
                      <FontAwesomeIcon icon={faArrowLeft} />
                    </button>
                    <button
                      onClick={handleNextChartPage}
                      disabled={!chartData.hasNext}
                      style={{
                        background: "rgba(255, 255, 255, 0.08)",
                        border: "1px solid rgba(255, 255, 255, 0.15)",
                        borderRadius: "6px",
                        color: chartData.hasNext ? "#fff" : "rgba(255, 255, 255, 0.3)",
                        padding: "6px 10px",
                        cursor: chartData.hasNext ? "pointer" : "default",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "0.85rem"
                      }}
                      title="Meses recentes"
                    >
                      <FontAwesomeIcon icon={faArrowRight} />
                    </button>
                  </div>
                )}
              </div>
              <div className="chart-container-wrapper">
                {showRevenue ? (
                  chartData.labels.length > 0 ? (
                    <div style={{ height: "300px", position: "relative" }}>
                      <Line data={chartDataConfig} options={chartOptions} />
                    </div>
                  ) : (
                    <p className="no-chart-data">Nenhum dado histórico encontrado para gerar o gráfico.</p>
                  )
                ) : (
                  <div className="chart-overlay-hidden">
                    <p>Gráfico oculto para privacidade.</p>
                    <p className="chart-overlay-sub">
                      Clique no ícone de olho (Mostrar faturamento) no topo para visualizar.
                    </p>
                  </div>
                )}
              </div>
            </div>

            <hr className="section-divider" />

            <div className="classes-values">
              <h3 className="clients-title-simple" style={{ color: "#fff", fontSize: "1.15rem", fontWeight: "600", textAlign: "left", marginTop: "20px", marginBottom: "10px" }}>Valores das aulas</h3>
              <div className="value-inputs">
                {defaultValues.map((value, index) => (
                  <div key={index}>
                    <label>
                      {index + 1} {index === 0 ? "Aula" : "Aulas"}
                    </label>
                    <input
                      type="number"
                      value={value}
                      onChange={(e) => {
                        const newValues = [...defaultValues];
                        newValues[index] = parseFloat(e.target.value) || 0;
                        setDefaultValues(newValues);
                      }}
                    />
                  </div>
                ))}
                <FontAwesomeIcon
                  icon={faSave}
                  className="icon save-icon"
                  onClick={handleSaveDefaultValues}
                  title="Salvar"
                />
              </div>
            </div>

            <hr className="section-divider" />

            <h3 className="clients-title-simple" style={{ color: "#fff", fontSize: "1.15rem", fontWeight: "600", textAlign: "left", marginTop: "20px", marginBottom: "10px" }}>Alunos Pendentes que faltam pagar</h3>

            <table className="unpayment-table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Aulas</th>
                </tr>
              </thead>
              <tbody>
                {currentUnpaidStudents.length > 0 ? (
                  currentUnpaidStudents.map((student) => (
                    <tr key={student.id}>
                      <td>{student.name}</td>
                      <td>{student.classes}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="2">Nenhum estudante encontrado.</td>
                  </tr>
                )}
              </tbody>
            </table>

            <div className="pagination">
              <button
                onClick={() =>
                  setCurrentPageUnpayment((prev) => Math.max(prev - 1, 1))
                }
                disabled={currentPageUnpayment === 1}
              >
                <FontAwesomeIcon icon={faArrowLeft} />
              </button>
              <div className="pagination-numbers">
                {renderDynamicPageNumbers(
                  currentPageUnpayment,
                  totalUnpaidPages,
                  handlePageChangeUnpayment
                )}
              </div>
              <button
                onClick={() =>
                  setCurrentPageUnpayment((prev) =>
                    Math.min(prev + 1, totalUnpaidPages)
                  )
                }
                disabled={currentPageUnpayment === totalUnpaidPages}
              >
                <FontAwesomeIcon icon={faArrowRight} />
              </button>
            </div>
          </div>
        )}

        {isAdding && (
          <div>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar por nome..."
            />



            <div className="date-selector" style={{ marginTop: "20px" }}>
              <input
                type="month"
                id="month-select-payment"
                value={selectedMonthPayment}
                onChange={(e) => setSelectedMonthPayment(e.target.value)}
              />
            </div>
            <table className="payment-table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Aulas</th>
                  <th>Pago?</th>
                  <th>Data</th>
                </tr>
              </thead>
              <tbody>
                {currentStudents.length > 0 ? (
                  currentStudents.map((student) => (
                    <tr key={student.id}>
                      <td>{student.name}</td>
                      <td>{student.classes}</td>
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedStudents[student.id] || false}
                          onChange={() => handlePaymentCheck(student.id)}
                        />
                      </td>
                      <td>
                        <select
                          className="day-select"
                          value={
                            student.paymentDay || currentDay
                          }
                          onChange={(e) =>
                            handleDayChange(student.id, e.target.value)
                          }
                        >
                          <option value="">Selecionar Dia</option>
                          {generateDays().map((day) => (
                            <option key={day} value={day}>
                              {day}
                            </option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4">Nenhum estudante encontrado.</td>
                  </tr>
                )}
              </tbody>
            </table>

            <div className="pagination">
              <button
                onClick={() =>
                  setCurrentPagePayment((prev) => Math.max(prev - 1, 1))
                }
                disabled={currentPagePayment === 1}
              >
                <FontAwesomeIcon icon={faArrowLeft} />
              </button>
              <div className="pagination-numbers">
                {renderDynamicPageNumbers(
                  currentPagePayment,
                  totalPages,
                  handlePageChangePayment
                )}
              </div>
              <button
                onClick={() =>
                  setCurrentPagePayment((prev) =>
                    Math.min(prev + 1, totalPages)
                  )
                }
                disabled={currentPagePayment === totalPages}
              >
                <FontAwesomeIcon icon={faArrowRight} />
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default PaymentControl;
